import { useEffect, useState, type ReactNode, type RefObject } from 'react';
import styles from './StickyCta.module.css';

interface StickyCtaProps {
  /**
   * The real, in-flow control this bar stands in for. The bar is up for exactly
   * as long as that control is below the fold, and stands down the moment it is
   * on screen — two copies of the same invitation competing for the same glance
   * is worse than one, and the real one is the better of the two.
   */
  standInFor: RefObject<HTMLElement | null>;
  /** Set false once the moment has passed — the bar is then never rendered. */
  enabled?: boolean;
  children: ReactNode;
}

/**
 * A bottom bar carrying the page's one real action, for the whole stretch of
 * the page where that action is out of reach.
 *
 * The rule is deliberately the simplest one that can be true: if she cannot see
 * the invitation, it is down here; if she can, it is not. Nothing about how far
 * she has read, no threshold to cross first — an offer that appears partway
 * down feels like it is reacting to her, which is exactly the wrong note.
 *
 * Where IntersectionObserver is missing the bar simply never shows. The real
 * control is untouched, so nothing is lost.
 */
export function StickyCta({ standInFor, enabled = true, children }: StickyCtaProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const target = standInFor.current;
    if (!target) return;

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        // Strictly below the fold. Once it has been scrolled up and past,
        // `top` is negative and the bar stays down: she has reached the real
        // invitation and following her past it would be nagging.
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top > 0);
      }
    });

    observer.observe(target);
    return () => observer.disconnect();
  }, [enabled, standInFor]);

  if (!enabled) return null;

  return (
    // Rendered last in the document, so it is the final tab stop rather than
    // an interruption, and made genuinely inert while hidden — an off-screen
    // duplicate of the main action is exactly the sort of thing that should
    // not be reachable by keyboard or announced.
    <div
      className={`${styles.bar} ${visible ? styles.visible : ''}`}
      aria-hidden={!visible}
      inert={!visible}
    >
      <div className={styles.inner}>{children}</div>
    </div>
  );
}
